// Build the CandidateSkill[] that Tempo evaluates each tick.
//
// A skill is "ready" when:
//   - we know how to look it up in the registry (otherwise drop it)
//   - cooldowns let it fire at the current tick
//   - the caster has enough MP
//   - the AI's currently-selected target sits within the skill's range
//
// We set `tags` based on the skill's source / shape / effects so Tempo's
// rule-based scoring (e.g. tempo_dot_focus, tempo_ult_focus) can prefer one
// kind over another.
import { getSkill } from '../content/skills.js';
const AUTO_ATTACK_KEY = '__auto_attack__';
/** Reserved cooldown key used by the auto-attack path. Exposed so the tick
 *  loop and tests can both reference it. */
export { AUTO_ATTACK_KEY };
/** Estimate the skill's punch for Tempo's scoring. */
function estimatePower(skill) {
    let dmg = 0;
    let other = 0;
    for (const effect of skill.effects) {
        if (effect.kind === 'damage')
            dmg += effect.power;
        else if (effect.kind === 'heal')
            other += effect.power * 0.6;
        else
            other += 0.2;
    }
    // Ultimates get a fixed bump so tempo_ult_focus can find them.
    return dmg + other + (skill.source === 'ultimate' ? 5 : 0);
}
function deriveTags(skill) {
    const tags = new Set();
    if (skill.source === 'ultimate')
        tags.add('ultimate');
    const shape = skill.targeting?.shape;
    if (shape === 'circle' || shape === 'cone' || shape === 'allAllies') {
        tags.add('aoe');
    }
    else if (shape === 'singleTarget' || shape === 'allyLowestHp' || shape === 'self') {
        tags.add('single');
    }
    if (skill.effects.some((e) => e.kind === 'applyDebuff'))
        tags.add('dot');
    return tags;
}
function distance(a, b) {
    const dx = a.pos.x - b.pos.x;
    const dy = a.pos.y - b.pos.y;
    return Math.hypot(dx, dy);
}
function lowestHpAlly(caster, state) {
    const allies = state.entities.filter((e) => e.side === caster.side && e.hp > 0);
    if (allies.length === 0)
        return null;
    let lowest = allies[0];
    for (let i = 1; i < allies.length; i++) {
        const e = allies[i];
        if (e.hp / e.hpMax < lowest.hp / lowest.hpMax)
            lowest = e;
    }
    return lowest;
}
export function isSkillInRange(skill, caster, target, state) {
    if (skill.range === undefined)
        return true;
    const range = skill.range;
    switch (skill.targeting?.shape) {
        case 'self':
            return true;
        case 'allyLowestHp': {
            const ally = lowestHpAlly(caster, state);
            return ally !== null && distance(caster, ally) <= range + caster.radius + ally.radius;
        }
        case 'allAllies':
            return state.entities.some((e) => e.side === caster.side &&
                e.hp > 0 &&
                distance(caster, e) <= range + caster.radius + e.radius);
        case 'singleTarget':
        case 'circle':
        case 'line':
        case 'cone':
        case undefined:
            return target !== null && distance(caster, target) <= range + caster.radius + target.radius;
    }
    return false;
}
/**
 * Build the candidate set for `entity`. Includes active itemSkills plus the
 * job's ultimate (if any). Filters out skills that aren't active cast skills;
 * trigger / reactive / passive skills fire through their own reducers.
 */
export function buildCandidates(entity, state, selectedTargetId) {
    const tick = state.tick;
    const target = selectedTargetId !== null ? state.entities.find((e) => e.id === selectedTargetId) : null;
    const ids = entity.itemSkills.map((it) => it.templateId);
    if (entity.ultimate)
        ids.push(entity.ultimate);
    const out = [];
    for (const id of ids) {
        const skill = getSkill(id);
        if (!skill)
            continue;
        if (skill.kind !== 'active')
            continue;
        const cdReady = (entity.cooldowns.get(id) ?? 0) <= tick;
        const ranged = isSkillInRange(skill, entity, target ?? null, state);
        out.push({
            id,
            ready: cdReady && ranged,
            tags: deriveTags(skill),
            power: estimatePower(skill),
            readyAt: entity.cooldowns.get(id) ?? 0,
        });
    }
    return out;
}
/** Compute the tick at which the entity's auto-attack should next fire. */
export function nextAutoAttackTick(entity, currentTick) {
    const intervalSeconds = entity.stats.attackInterval;
    const speedMod = 1 + entity.speedMods.actionSpeed + entity.speedMods.attackSpeed;
    const ticksPerAttack = Math.max(1, Math.round((intervalSeconds * 30) / Math.max(0.2, speedMod)));
    return currentTick + ticksPerAttack;
}
//# sourceMappingURL=candidates.js.map